// src/pages/categoryItem/components/documents/Documents.tsx
import React, { useState } from "react";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileLines,
  FaFile,
  FaDownload,
} from "react-icons/fa6";
import BottomModal from "../../../common/components/bottomModal/BottomModal";
import { useDocuments } from "./useDocuments";
import styles from "./Documents.module.scss";
import { EntityType } from "../../../common/types/enums";
import { DocumentInterface } from "../../../common/types/types";
import { getDocumentFileUrl } from "../../../common/utils/functions/getDocumentFileUrl";
import { shortenText } from "../../../common/utils/functions/shortenText";

type Props = {
  entityType: EntityType;
  entityId?: number;
  noTitle?: boolean;
  embedded?: boolean;
};

const getIcon = (mimeType?: string) => {
  if (mimeType === "application/pdf")
    return { Icon: FaFilePdf, color: "#e53935" };
  if (
    mimeType === "application/msword" ||
    mimeType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
    return { Icon: FaFileWord, color: "#1e88e5" };
  if (
    mimeType === "application/vnd.ms-excel" ||
    mimeType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  )
    return { Icon: FaFileExcel, color: "#43a047" };
  if (mimeType === "text/plain") return { Icon: FaFileLines, color: "#616161" };
  return { Icon: FaFile, color: "#757575" };
};

export const Documents = ({
  entityType,
  entityId,
  noTitle,
  embedded,
}: Props) => {
  const { documents } = useDocuments(entityType, entityId);
  const [previewDoc, setPreviewDoc] = useState<DocumentInterface | null>(null);

  if (!documents.length) return null;

  return (
    <div
      className={`${styles.container} ${embedded ? styles.containerEmbedded : ""}`}
    >
      {!noTitle && <h3 className={styles.title}>Документы</h3>}

      <div className={styles.list}>
        {documents.map(({ document }) => {
          const fileUrl = getDocumentFileUrl(document);
          const isImage = document.mimeType?.startsWith("image/");
          const { Icon, color } = getIcon(document.mimeType);

          return (
            <div key={document.id} className={styles.item}>
              <div
                className={styles.preview}
                onClick={() => isImage && setPreviewDoc(document)}
                style={{ cursor: isImage ? "pointer" : "default" }}
              >
                {isImage ? (
                  <img
                    src={fileUrl}
                    alt={document.name}
                    className={styles.image}
                  />
                ) : (
                  <Icon size={40} color={color} />
                )}
              </div>

              <div className={styles.info}>
                <div className={styles.name}>
                  {shortenText(document.name, 60)}
                </div>
                {document.description && (
                  <div className={styles.description}>
                    {shortenText(document.description, 100)}
                  </div>
                )}
              </div>

              <a
                href={fileUrl}
                download
                className={styles.download}
                aria-label={`Скачать ${document.name}`}
              >
                <FaDownload size={18} />
              </a>
            </div>
          );
        })}
      </div>

      <BottomModal isVisible={!!previewDoc} onClose={() => setPreviewDoc(null)}>
        {previewDoc && (
          <div className={styles.previewModal}>
            <img
              src={getDocumentFileUrl(previewDoc)}
              alt={previewDoc.name}
              className={styles.previewImage}
            />
            <div className={styles.previewName}>{previewDoc.name}</div>
          </div>
        )}
      </BottomModal>
    </div>
  );
};
